import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./PianificazioneAbiti.css";
import RotateDevice from "../components/RotateDevice/RotateDevice";
import Lf23Loader from "../components/Lf23Loader";

const PianificazioneAbiti = () => {
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    // Funzione per calcolare il numero della settimana corrente (ISO 8601)
    const getWeekNumber = (date) => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    };

    // Funzione per calcolare le date di inizio e fine settimana da un numero di settimana (ISO 8601)
    const getWeekDates = (weekNumber, year = new Date().getFullYear()) => {
        // Primo giorno dell'anno
        const jan4 = new Date(Date.UTC(year, 0, 4));
        // Trova il lunedì della settimana 1
        const week1Monday = new Date(jan4);
        week1Monday.setUTCDate(jan4.getUTCDate() - (jan4.getUTCDay() || 7) + 1);

        // Calcola il lunedì della settimana richiesta
        const targetMonday = new Date(week1Monday);
        targetMonday.setUTCDate(week1Monday.getUTCDate() + (weekNumber - 1) * 7);

        // Calcola la domenica (lunedì + 6 giorni)
        const targetSunday = new Date(targetMonday);
        targetSunday.setUTCDate(targetMonday.getUTCDate() + 6);

        return {
            start: targetMonday,
            end: targetSunday
        };
    };

    // Funzione per formattare una data in italiano
    const formatDateItalian = (date) => {
        const months = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
        const day = date.getUTCDate();
        const month = months[date.getUTCMonth()];
        const year = date.getUTCFullYear();
        return `${day} ${month} ${year}`;
    };

    // Funzione per ottenere il range leggibile di una settimana
    const getWeekRangeLabel = (weekNumber) => {
        const dates = getWeekDates(weekNumber);
        const startDay = dates.start.getUTCDate();
        const endDay = dates.end.getUTCDate();
        const months = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
        const startMonth = months[dates.start.getUTCMonth()];
        const endMonth = months[dates.end.getUTCMonth()];
        const year = dates.start.getUTCFullYear();

        if (dates.start.getUTCMonth() === dates.end.getUTCMonth()) {
            return `${startDay}-${endDay} ${startMonth} ${year}`;
        } else {
            return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
        }
    };

    // Calcola settimana corrente e range default (ultime 10 settimane)
    const getCurrentWeek = () => getWeekNumber(new Date());
    const currentWeek = getCurrentWeek();
    const defaultWeekFrom = Math.max(1, currentWeek - 9); // Ultime 10 settimane
    const defaultWeekTo = currentWeek;

    const [settimane, setSettimane] = useState([]);
    const [percorsi, setPercorsi] = useState([]);
    const [stats, setStats] = useState({
        totale_percorsi: 0,
        totale_campane: 0,
        campane_svuotate: 0,
        campane_non_svuotate: 0,
        settimane_attive: 0,
        settimana_da: null,
        settimana_a: null
    });
    const [loading, setLoading] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [expandedPercorso, setExpandedPercorso] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [hoveredWeek, setHoveredWeek] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [fullscreenCard, setFullscreenCard] = useState(null);

    // Escape per uscire dal fullscreen
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && fullscreenCard) setFullscreenCard(null);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [fullscreenCard]);

    // Filtri - Imposta default per le settimane
    const [nomePercorsoFilter, setNomePercorsoFilter] = useState("");
    const [settimanaDa, setSettimanaDa] = useState(defaultWeekFrom.toString());
    const [settimanaA, setSettimanaA] = useState(defaultWeekTo.toString());

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {};

            // Applica sempre i filtri se sono impostati
            if (nomePercorsoFilter.trim()) {
                params.nomePercorso = nomePercorsoFilter.trim();
            }
            if (settimanaDa && settimanaA) {
                params.settimanaDa = settimanaDa;
                params.settimanaA = settimanaA;
            }

            const [dataRes, statsRes] = await Promise.all([
                axios.get(`${API_URL}/interacta/pianificazione-abiti`, { params }),
                axios.get(`${API_URL}/interacta/pianificazione-abiti/stats`, { params })
            ]);

            if (dataRes.data.success) {
                setSettimane(dataRes.data.data.settimane || []);
                setPercorsi(dataRes.data.data.percorsi || []);
            }

            if (statsRes.data.success) {
                setStats(statsRes.data.data);
            }
        } catch (err) {
            console.error("Errore nel caricamento dati:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, []);

    const togglePercorso = (nomePercorso) => {
        setExpandedPercorso(expandedPercorso === nomePercorso ? null : nomePercorso);
    };

    const handleWeekHover = (settimana, event) => {
        if (event) {
            const rect = event.currentTarget.getBoundingClientRect();
            setTooltipPosition({
                x: rect.left + rect.width / 2,
                y: rect.top - 10
            });
        }
        setHoveredWeek(settimana);
    };

    const handleWeekLeave = () => {
        setHoveredWeek(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validazione range settimane
        if (settimanaDa && settimanaA) {
            if (parseInt(settimanaDa) > parseInt(settimanaA)) {
                alert("La settimana iniziale non può essere maggiore della settimana finale.");
                return;
            }
        }

        // Ricarica i dati con i filtri correnti
        fetchData();
    };

    const handleReset = async () => {
        // Reset dei filtri ai valori di default
        setNomePercorsoFilter("");
        setSettimanaDa(defaultWeekFrom.toString());
        setSettimanaA(defaultWeekTo.toString());
        setSearchTerm("");

        // Ricarica dati con default settimane
        setLoading(true);
        try {
            const params = {
                settimanaDa: defaultWeekFrom.toString(),
                settimanaA: defaultWeekTo.toString()
            };

            const [dataRes, statsRes] = await Promise.all([
                axios.get(`${API_URL}/interacta/pianificazione-abiti`, { params }),
                axios.get(`${API_URL}/interacta/pianificazione-abiti/stats`, { params })
            ]);

            if (dataRes.data.success) {
                setSettimane(dataRes.data.data.settimane || []);
                setPercorsi(dataRes.data.data.percorsi || []);
            }

            if (statsRes.data.success) {
                setStats(statsRes.data.data);
            }
        } catch (err) {
            console.error("Errore nel reset:", err);
        } finally {
            setLoading(false);
        }
    };

    const filteredPercorsi = percorsi.filter(percorso => {
        if (!searchTerm) return true;
        return percorso.nome_percorso.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const formatData = (dataStr) => {
        if (!dataStr) return "-";
        const date = new Date(dataStr);
        return date.toLocaleDateString("it-IT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    // Raggruppa le campane per nome (rimuovendo duplicati)
    const getCampaneUniche = (settimana, dettaglioCampane) => {
        if (!dettaglioCampane) return [];

        const campaneMap = new Map();
        dettaglioCampane.forEach(campana => {
            if (!campaneMap.has(campana.id_campana)) {
                campaneMap.set(campana.id_campana, {
                    id: campana.id_campana,
                    nome: campana.nome_campana,
                    data_svuotamento: campana.data_svuotamento
                });
            }
        });

        return Array.from(campaneMap.values()).sort((a, b) =>
            a.nome.localeCompare(b.nome)
        );
    };

    // Ottieni tutte le campane di un percorso (unione di tutte le settimane)
    const getTutteCampanePercorso = (percorso) => {
        const campaneMap = new Map();

        settimane.forEach(settimana => {
            const datiSettimana = percorso.settimane[settimana];
            if (datiSettimana && datiSettimana.dettaglio_campane) {
                datiSettimana.dettaglio_campane.forEach(campana => {
                    if (!campaneMap.has(campana.id_campana)) {
                        campaneMap.set(campana.id_campana, {
                            id: campana.id_campana,
                            nome: campana.nome_campana,
                            ordine: campana.ordine_campane_percorso || 999999
                        });
                    }
                });
            }
        });

        return Array.from(campaneMap.values()).sort((a, b) =>
            a.ordine - b.ordine
        );
    };

    // Trova i dati di svuotamento di una specifica campana per una settimana
    const getDataSvuotamentoCampana = (percorso, settimana, idCampana) => {
        const datiSettimana = percorso.settimane[settimana];
        if (!datiSettimana || !datiSettimana.dettaglio_campane) return null;

        const campana = datiSettimana.dettaglio_campane.find(c => c.id_campana === idCampana);
        return campana ? campana.data_svuotamento : null;
    };

    return (
        <div className="pianificazione-abiti-container">
            {/* Header */}
            <header className="page-header-abiti">
                <div className="header-content-abiti">
                    <button className="back-button-abiti" onClick={() => navigate('/produttivita')}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                        Indietro
                    </button>

                    <div className="header-title-abiti">
                        <div className="header-title-row-abiti">
                            <h2>Pianificazione Abiti</h2>
                            <button className="info-button-abiti" onClick={() => setShowInfo(!showInfo)} title="Informazioni">
                                <span className="info-icon-text-abiti">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle-abiti">Monitoraggio settimanale percorsi e campane</p>
                    </div>
                </div>
            </header>

            {/* Modal Info */}
            {showInfo && (
                <div className="modal-overlay-abiti" onClick={() => setShowInfo(false)}>
                    <div className="modal-container-abiti" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-abiti">
                            <div className="modal-icon-abiti">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                                    <path d="M10 8h6"></path>
                                    <path d="M10 12h6"></path>
                                    <path d="M10 16h4"></path>
                                </svg>
                            </div>
                            <h3>Informazioni sulla pagina</h3>
                            <button className="modal-close-abiti" onClick={() => setShowInfo(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body-abiti">
                            <div className="modal-section-abiti">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Questa pagina mostra la <strong>pianificazione settimanale degli svuotamenti</strong> delle campane per ogni percorso.
                                    Puoi visualizzare lo stato delle campane per le <strong>ultime 10 settimane</strong> e verificare quali campane sono state svuotate.
                                </p>
                            </div>
                            <div className="modal-section-abiti">
                                <h4>Come leggere i dati?</h4>
                                <ul style={{ marginLeft: '20px', marginTop: '10px', lineHeight: '1.8' }}>
                                    <li><strong>Vista principale:</strong> Mostra per ogni percorso il numero totale di campane e quelle non svuotate per settimana</li>
                                    <li><strong>Icona verde (✓):</strong> La campana è stata svuotata</li>
                                    <li><strong>Icona rossa (⚠):</strong> La campana NON è stata svuotata</li>
                                    <li><strong>Espandi dettaglio:</strong> Clicca su un percorso per vedere il dettaglio di ogni singola campana</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Content Area */}
            <div className="content-area-abiti">
                {/* Toggle filtri */}
                <button
                    className={`toggle-filters-btn-abiti ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                    </svg>
                    {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
                </button>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="filters-panel-abiti">
                        <form className="filters-form-abiti" onSubmit={handleSubmit}>
                            <div className="filters-grid-abiti">
                                <div className="filter-item-abiti">
                                    <label>Nome Percorso</label>
                                    <input
                                        type="text"
                                        placeholder="Es: Bologna, Percorso 01..."
                                        value={nomePercorsoFilter}
                                        onChange={(e) => setNomePercorsoFilter(e.target.value)}
                                    />
                                </div>

                                <div className="filter-item-abiti">
                                    <label>Settimana Da</label>
                                    <input
                                        type="number"
                                        placeholder="Es: 1"
                                        min="1"
                                        max="53"
                                        value={settimanaDa}
                                        onChange={(e) => setSettimanaDa(e.target.value)}
                                    />
                                </div>

                                <div className="filter-item-abiti">
                                    <label>Settimana A</label>
                                    <input
                                        type="number"
                                        placeholder="Es: 52"
                                        min="1"
                                        max="53"
                                        value={settimanaA}
                                        onChange={(e) => setSettimanaA(e.target.value)}
                                    />
                                </div>

                                <div className="filter-item-abiti filter-actions-abiti">
                                    <label>&nbsp;</label>
                                    <div className="filter-buttons-abiti">
                                        <button type="submit" className="btn-apply-abiti">Applica</button>
                                        <button type="button" className="btn-reset-abiti" onClick={handleReset}>Reset</button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {/* Search + Results */}
                <div className="top-controls-abiti">
                    <div className="controls-left-abiti">
                        <div className="search-bar-abiti">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input
                                type="text"
                                placeholder="Cerca percorso..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="controls-right-abiti">
                        <div className="results-badge-abiti">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="7" height="7"></rect>
                                <rect x="14" y="3" width="7" height="7"></rect>
                                <rect x="14" y="14" width="7" height="7"></rect>
                                <rect x="3" y="14" width="7" height="7"></rect>
                            </svg>
                            <span>{filteredPercorsi.length} percorsi</span>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="stats-grid-abiti">
                    <div className="stat-card-abiti stat-primary-abiti">
                        <div className="stat-icon-abiti">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 10H3M16 2v4M8 2v4M3 8h18v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8z" />
                            </svg>
                        </div>
                        <div className="stat-content-abiti">
                            <p className="stat-label-abiti">Totale Percorsi</p>
                            <p className="stat-value-abiti">{stats.totale_percorsi}</p>
                        </div>
                    </div>

                    <div className="stat-card-abiti stat-success-abiti">
                        <div className="stat-icon-abiti">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                        </div>
                        <div className="stat-content-abiti">
                            <p className="stat-label-abiti">Campane Svuotate</p>
                            <p className="stat-value-abiti">{stats.campane_svuotate}</p>
                        </div>
                    </div>

                    <div className="stat-card-abiti stat-warning-abiti">
                        <div className="stat-icon-abiti">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                <line x1="12" y1="9" x2="12" y2="13" />
                                <line x1="12" y1="17" x2="12.01" y2="17" />
                            </svg>
                        </div>
                        <div className="stat-content-abiti">
                            <p className="stat-label-abiti">Campane Non Svuotate</p>
                            <p className="stat-value-abiti">{stats.campane_non_svuotate}</p>
                        </div>
                    </div>

                    <div className="stat-card-abiti stat-info-abiti">
                        <div className="stat-icon-abiti">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                        </div>
                        <div className="stat-content-abiti">
                            <p className="stat-label-abiti">Settimane Selezionate</p>
                            <p className="stat-value-abiti">
                                {stats.settimane_attive}
                                {stats.settimana_da && stats.settimana_a && (
                                    <span className="stat-range-abiti"> (S{stats.settimana_da} - S{stats.settimana_a})</span>
                                )}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Loading */}
                {loading && (
                    <Lf23Loader />
                )}

                {/* Percorsi List */}
                {!loading && filteredPercorsi.length > 0 && (
                    <div className="percorsi-list-abiti">
                        {filteredPercorsi.map((percorso, index) => (
                            <div key={index} className={`percorso-card-abiti${fullscreenCard === percorso.nome_percorso ? ' fullscreen-card-abiti' : ''}`}>
                                {/* Header Percorso */}
                                <div className="percorso-header-abiti" onClick={() => togglePercorso(percorso.nome_percorso)}>
                                    <div className="percorso-title-abiti">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <h3>{percorso.nome_percorso}</h3>
                                            <span className="badge-campane-percorso-abiti">
                                                {percorso.totale_campane_percorso} campane
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <button
                                                className="fullscreen-btn-abiti"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setFullscreenCard(fullscreenCard === percorso.nome_percorso ? null : percorso.nome_percorso);
                                                    if (expandedPercorso !== percorso.nome_percorso) setExpandedPercorso(percorso.nome_percorso);
                                                }}
                                                title={fullscreenCard === percorso.nome_percorso ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    {fullscreenCard === percorso.nome_percorso ? (
                                                        <>
                                                            <polyline points="4 14 10 14 10 20"></polyline>
                                                            <polyline points="20 10 14 10 14 4"></polyline>
                                                            <line x1="14" y1="10" x2="21" y2="3"></line>
                                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <polyline points="15 3 21 3 21 9"></polyline>
                                                            <polyline points="9 21 3 21 3 15"></polyline>
                                                            <line x1="21" y1="3" x2="14" y2="10"></line>
                                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                                        </>
                                                    )}
                                                </svg>
                                            </button>
                                            <button className={`expand-button-abiti ${expandedPercorso === percorso.nome_percorso ? 'expanded' : ''}`}>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="6 9 12 15 18 9"></polyline>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Summary Settimane */}
                                <div className="settimane-summary-abiti">
                                    {settimane.map(settimana => {
                                        const dati = percorso.settimane[settimana];
                                        const hasDati = !!dati;
                                        const nonSvuotate = hasDati ? dati.campane_non_svuotate : 0;
                                        const totale = percorso.totale_campane_percorso || 0;
                                        const percentuale = totale > 0 ? ((totale - nonSvuotate) / totale * 100) : 0;

                                        return (
                                            <div
                                                key={settimana}
                                                className="settimana-badge-abiti"
                                                onMouseEnter={(e) => handleWeekHover(settimana, e)}
                                                onMouseLeave={handleWeekLeave}
                                            >
                                                <div className="settimana-label-abiti">S{settimana}</div>
                                                <div className="settimana-stats-abiti">
                                                    {nonSvuotate > 0 ? (
                                                        <span className="badge-alert-abiti" title="Campane non svuotate">
                                                            ⚠ {nonSvuotate}
                                                        </span>
                                                    ) : (
                                                        <span className="badge-success-abiti" title="Tutte svuotate">✓</span>
                                                    )}
                                                </div>
                                                <div className="progress-bar-mini-abiti">
                                                    <div
                                                        className="progress-fill-mini-abiti"
                                                        style={{ width: `${percentuale}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Dettaglio Campane (Accordion) */}
                                {expandedPercorso === percorso.nome_percorso && (
                                    <div className="dettaglio-campane-abiti">
                                        <RotateDevice active={fullscreenCard === percorso.nome_percorso} />
                                        <div className="table-wrapper-abiti">
                                            <table className="campane-table-abiti">
                                                <thead>
                                                    <tr>
                                                        <th className="sticky-col-abiti">Campana</th>
                                                        {settimane.map(settimana => (
                                                            <th key={settimana} className="settimana-col-abiti">
                                                                S{settimana}: {getWeekRangeLabel(settimana)}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {getTutteCampanePercorso(percorso).map((campana, campanaIndex) => (
                                                        <tr key={campanaIndex}>
                                                            <td className="sticky-col-abiti campana-name-abiti">
                                                                {campana.nome}
                                                            </td>
                                                            {settimane.map(settimana => {
                                                                const dataSvuotamento = getDataSvuotamentoCampana(
                                                                    percorso,
                                                                    settimana,
                                                                    campana.id
                                                                );

                                                                return (
                                                                    <td key={settimana} className="settimana-cell-abiti">
                                                                        {dataSvuotamento !== null ? (
                                                                            dataSvuotamento ? (
                                                                                <div className="cell-svuotato-abiti" title={formatData(dataSvuotamento)}>
                                                                                    <span className="icon-success-abiti">✓</span>
                                                                                    <span className="data-text-abiti">{formatData(dataSvuotamento)}</span>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="cell-non-svuotato-abiti" title="Non svuotato">
                                                                                    <span className="icon-warning-abiti">⚠</span>
                                                                                </div>
                                                                            )
                                                                        ) : (
                                                                            <div className="cell-empty-abiti">-</div>
                                                                        )}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Empty State */}
                {!loading && filteredPercorsi.length === 0 && (
                    <div className="empty-state-abiti">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                        </svg>
                        <h3>Nessun percorso trovato</h3>
                        <p>Prova a modificare i criteri di ricerca</p>
                    </div>
                )}
            </div>

            {/* Tooltip per settimane */}
            {hoveredWeek !== null && (
                <div
                    className="tooltip-week-abiti"
                    style={{
                        position: 'fixed',
                        left: `${tooltipPosition.x}px`,
                        top: `${tooltipPosition.y}px`,
                        transform: 'translate(-50%, -100%)',
                        zIndex: 10000
                    }}
                >
                    Settimana {hoveredWeek}: {getWeekRangeLabel(hoveredWeek)}
                </div>
            )}
        </div>
    );
};

export default PianificazioneAbiti;
